// ── Friet Fighter — procedurele attack-poses ──
// De "klassieke" fighting-game-methode: hand-keyed pose-to-pose, getimed op de
// frame data — NIET mocap. Een aanval = een paar extreme houdingen die op de
// juiste sim-frames worden vastgehouden:
//   windup (coil)  → SNAP naar slag-extreme → HOLD op de active-frames → recovery.
// De snap + hold is precies wat tijd-gecomprimeerde mocap niet kan leveren.
//
// Poses zijn DATA: per move een lijst bone-delta's (axis-angle bovenop de rust-
// houding). Tunen = getallen aanpassen, geen code. Render leest sim (stateFrame),
// schrijft er nooit naar.

import type { Move } from '../sim';

/** Eén bone-rotatie: `angle` rad rond lokale `axis`, bovenop de rust-quaternion. */
export interface BoneDelta {
  bone: string;
  axis: readonly [number, number, number];
  angle: number; // radialen bij vol gewicht
}

export interface ProcMove {
  anticip: BoneDelta[]; // windup-coil, piekt aan het eind van startup
  strike: BoneDelta[]; // slag-extreme, vastgehouden door de active-frames
}

const D = (bone: string, axis: [number, number, number], angle: number): BoneDelta => ({
  bone,
  axis,
  angle,
});

// ── standHeavy "Zware Klap" — rechte rechtse stoot (cross) ──
// Rust-houding is al een boks-stance. De cross: torso draait mee, rechterarm
// zwaait voorwaarts en strekt de elleboog, off-arm trekt in de guard.
// Hoeken in radialen; empirisch gekalibreerd via scripts/pose-capture.mjs.
// Bone-assen empirisch gemeten (scripts/pose-calib.mjs): RightArm Z- = arm
// vooruit (cross), RightForeArm X- = elleboog strekken. Y-twist op hips/spine
// draait de schouder juist wég → in 2.5D leest een rechte voorwaartse arm beter.
export const PROC_MOVES: Record<string, ProcMove> = {
  standHeavy: {
    anticip: [
      D('RightArm', [0, 0, 1], 0.35), // chamber: slagarm trekt terug
      D('Spine', [1, 0, 0], -0.08), // lichte coil naar achter
    ],
    strike: [
      D('Spine', [1, 0, 0], 0.18), // voorwaartse lean (gewicht in de stoot)
      D('RightShoulder', [0, 0, 1], -0.15), // schouder rolt mee
      D('RightArm', [0, 0, 1], -1.62), // slagarm strekt voorwaarts (cross)
      D('RightForeArm', [1, 0, 0], -0.35), // elleboog strekt door
      D('LeftArm', [0, 0, 1], 0.25), // off-arm blijft in de guard
    ],
  },
};

export interface PhaseWeights {
  wA: number; // anticipatie-gewicht
  wS: number; // strike-gewicht
}

const easeOut = (t: number): number => 1 - (1 - t) * (1 - t);

/**
 * Weeg windup vs strike op basis van het sim-frame in de move.
 * - startup-venster: coil bouwt op (rust → anticip).
 * - active-venster:  SNAP naar strike, vol vastgehouden (de klap).
 * - recovery:        strike → rust.
 */
export function evalPhase(m: Move, stateFrame: number): PhaseWeights {
  const s = Math.max(1, m.startup);
  const activeStart = m.startup;
  const activeEnd = m.startup + m.active;
  const r = Math.max(1, m.recovery);

  if (stateFrame < activeStart) {
    return { wA: easeOut(Math.min(1, stateFrame / s)), wS: 0 };
  }
  if (stateFrame < activeEnd) {
    return { wA: 0, wS: 1 }; // snap + hold
  }
  const k = Math.min(1, (stateFrame - activeEnd) / r);
  return { wA: 0, wS: 1 - easeOut(k) };
}

// ── Volledige procedurele actie-set ──────────────────────────────────────────
// Gedeeld door de hele cast (identiek 24-bone skelet). Vervangt de mocap-clips
// die op stompe snack-proporties niet passen. Arm-assen gekalibreerd:
//   RightArm Z- = vooruit, X+ = omhoog; RightForeArm X- = elleboog strekken.
//   Links spiegelt (Z+ = vooruit). Been: UpLeg X = sagittale heupzwaai,
//   Leg X = knie. Spine X+ = voorwaartse lean. (signs geverifieerd in het lab.)
const sin = Math.sin;

export type ActionKind = 'loop' | 'hold' | 'attack';
export interface ActionDef {
  kind: ActionKind;
  hold?: BoneDelta[]; // 'hold': vaste pose
  loop?: (t: number) => BoneDelta[]; // 'loop': cyclische pose (t = sec)
  anticip?: BoneDelta[]; // 'attack': chamber
  strike?: BoneDelta[]; // 'attack': extreme
  startup?: number;
  active?: number;
  recovery?: number;
  // Cartoon squash/stretch (Y-schaal): <1 = hurken, >1 = uitrekken. Voor blobby
  // snack-mascots leesbaarder dan knie-IK (voeten blijven plat via grounding).
  squash?: number;
}

const scaleDeltas = (ds: BoneDelta[], w: number): BoneDelta[] =>
  w >= 0.999 ? ds : ds.map((d) => ({ ...d, angle: d.angle * w }));

export const ACTIONS: Record<string, ActionDef> = {
  idle: {
    kind: 'loop',
    loop: (t) => {
      const b = sin(t * 1.6) * 0.03;
      return [D('Spine', [1, 0, 0], b), D('Spine01', [1, 0, 0], b * 0.5), D('neck', [1, 0, 0], -b * 0.6)];
    },
  },
  walk: {
    kind: 'loop',
    // Bescheiden been-zwaai (voet-IK ontbreekt → kleine amplitude voorkomt glijden)
    // + cartoon-waggle (zijwaartse lean) — past bij een blobby snack.
    loop: (t) => {
      const p = t * 7;
      const sw = sin(p);
      return [
        D('RightUpLeg', [1, 0, 0], -sw * 0.32),
        D('LeftUpLeg', [1, 0, 0], -sw * 0.32),
        D('RightLeg', [1, 0, 0], Math.max(0, -sw) * 0.45),
        D('LeftLeg', [1, 0, 0], Math.max(0, sw) * 0.45),
        D('RightArm', [0, 0, 1], sw * 0.2),
        D('LeftArm', [0, 0, 1], -sw * 0.2),
        D('Spine', [0, 0, 1], sw * 0.06), // waggle
      ];
    },
  },
  crouch: {
    kind: 'hold',
    squash: 0.62, // cartoon-squash i.p.v. knie-IK
    hold: [D('Spine', [1, 0, 0], 0.18)],
  },
  block: {
    kind: 'hold',
    hold: [
      D('RightArm', [0, 0, 1], -0.5),
      D('LeftArm', [0, 0, 1], 0.5),
      D('RightForeArm', [1, 0, 0], -1.1),
      D('LeftForeArm', [1, 0, 0], -1.1),
      D('Spine', [1, 0, 0], -0.12),
    ],
  },
  jump: {
    kind: 'hold',
    squash: 1.12, // uitrekken (sprong-stretch)
    hold: [
      D('RightArm', [0, 0, 1], -0.7), // armen omhoog (Z mirror)
      D('LeftArm', [0, 0, 1], 0.7),
      D('RightForeArm', [1, 0, 0], -0.4),
      D('LeftForeArm', [1, 0, 0], -0.4),
    ],
  },
  // ── Attacks ──
  standLight: {
    kind: 'attack',
    startup: 4,
    active: 3,
    recovery: 8,
    anticip: [D('LeftArm', [0, 0, 1], -0.2)],
    strike: [
      D('LeftArm', [0, 0, 1], 1.4), // lead jab (linkerarm vooruit)
      D('LeftForeArm', [1, 0, 0], -0.3),
      D('Spine', [1, 0, 0], 0.08),
      D('RightArm', [0, 0, 1], -0.3), // off-arm guard
    ],
  },
  standHeavy: {
    kind: 'attack',
    startup: 9,
    active: 4,
    recovery: 18,
    anticip: PROC_MOVES.standHeavy.anticip,
    strike: PROC_MOVES.standHeavy.strike,
  },
  crouchLight: {
    kind: 'attack',
    startup: 4,
    active: 3,
    recovery: 9,
    squash: 0.62, // gehurkt (squash) + lage por
    anticip: [],
    strike: [
      D('RightArm', [0, 0, 1], -1.2), // lage por vooruit
      D('RightForeArm', [1, 0, 0], -0.3),
    ],
  },
  kick: {
    kind: 'attack',
    startup: 7,
    active: 4,
    recovery: 14,
    anticip: [D('RightUpLeg', [1, 0, 0], 0.3)],
    strike: [
      D('RightUpLeg', [1, 0, 0], -1.1), // been vooruit omhoog
      D('RightLeg', [1, 0, 0], -0.3), // strekt door
      D('Spine', [1, 0, 0], -0.15), // tegenwicht
      D('LeftArm', [0, 0, 1], 0.4),
      D('RightArm', [0, 0, 1], -0.3),
    ],
  },
  super: {
    kind: 'attack',
    startup: 8,
    active: 10,
    recovery: 24,
    anticip: [D('Spine', [1, 0, 0], -0.15), D('RightArm', [0, 0, 1], 0.4), D('LeftArm', [0, 0, 1], -0.4)],
    strike: [
      D('Spine', [1, 0, 0], 0.2),
      D('RightArm', [0, 0, 1], -1.7),
      D('RightForeArm', [1, 0, 0], -0.4),
      D('LeftArm', [0, 0, 1], 1.7),
      D('LeftForeArm', [1, 0, 0], -0.4),
    ],
  },
  // ── Per-fighter signature specials ──────────────────────────────────────────
  // Geanimeerd per archetype (projectiel-cast / dash-lunge / grab-roll / utility),
  // gekeyd op de sim-move-id zodat character.actionId() ze automatisch oppikt.
  // startup/active/recovery = de sim-frame-data (movesets.ts) → frame-accuraat scrubben.
  // Projectiel — Frikandel Speciaal: klassieke twee-hands-stoot vooruit (hadouken).
  frikandelSpeciaal: {
    kind: 'attack', startup: 12, active: 6, recovery: 18,
    anticip: [D('RightArm', [0, 0, 1], 0.45), D('LeftArm', [0, 0, 1], -0.45), D('RightForeArm', [1, 0, 0], -0.6), D('LeftForeArm', [1, 0, 0], -0.6), D('Spine', [1, 0, 0], -0.12)],
    strike: [D('Spine', [1, 0, 0], 0.16), D('RightArm', [0, 0, 1], -1.25), D('LeftArm', [0, 0, 1], 1.25), D('RightForeArm', [1, 0, 0], -0.45), D('LeftForeArm', [1, 0, 0], -0.45)],
  },
  // Projectiel — Ragout-golf: rijzende schep-golf (armen omhoog-vooruit).
  ragoutGolf: {
    kind: 'attack', startup: 14, active: 6, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.4), D('LeftArm', [0, 0, 1], -0.4), D('Spine', [1, 0, 0], -0.1)],
    strike: [D('Spine', [1, 0, 0], 0.1), D('RightArm', [1, 0, 0], 0.5), D('RightArm', [0, 0, 1], -0.9), D('LeftArm', [1, 0, 0], 0.5), D('LeftArm', [0, 0, 1], 0.9), D('RightForeArm', [1, 0, 0], -0.3), D('LeftForeArm', [1, 0, 0], -0.3)],
  },
  // Projectiel — Kaas-flik: snelle één-hands flits.
  kaasFlik: {
    kind: 'attack', startup: 10, active: 5, recovery: 22,
    anticip: [D('RightArm', [0, 0, 1], 0.4), D('RightForeArm', [1, 0, 0], -0.2)],
    strike: [D('RightArm', [0, 0, 1], -1.45), D('RightForeArm', [1, 0, 0], -0.55), D('Spine', [1, 0, 0], 0.1), D('LeftArm', [0, 0, 1], 0.3)],
  },
  // Dash — Verre Port: diepe schermutsel-uitval met de prikker (lange reik).
  verrePort: {
    kind: 'attack', startup: 11, active: 5, recovery: 22,
    anticip: [D('RightArm', [0, 0, 1], 0.3), D('Spine', [1, 0, 0], -0.1), D('RightUpLeg', [1, 0, 0], 0.2)],
    strike: [D('Spine', [1, 0, 0], 0.28), D('RightArm', [0, 0, 1], -1.7), D('RightForeArm', [1, 0, 0], -0.5), D('LeftArm', [0, 0, 1], 0.3), D('LeftUpLeg', [1, 0, 0], 0.5), D('LeftLeg', [1, 0, 0], 0.5), D('RightUpLeg', [1, 0, 0], 0.4)],
  },
  // Dash — Zoef: gestroomlijnde flits-lunge (lijf naar voren, armen naar achter).
  zoef: {
    kind: 'attack', startup: 6, active: 4, recovery: 16, squash: 1.05,
    anticip: [D('Spine', [1, 0, 0], -0.1), D('RightArm', [0, 0, 1], 0.5), D('LeftArm', [0, 0, 1], -0.5)],
    strike: [D('Spine', [1, 0, 0], 0.32), D('neck', [1, 0, 0], 0.18), D('RightArm', [0, 0, 1], 0.55), D('LeftArm', [0, 0, 1], -0.55), D('RightForeArm', [1, 0, 0], -0.3), D('LeftForeArm', [1, 0, 0], -0.3), D('RightUpLeg', [1, 0, 0], -0.3), D('LeftUpLeg', [1, 0, 0], 0.3)],
  },
  // Command-grab — Bowling-rol: ineengedoken bal-rol vooruit.
  bowlingRol: {
    kind: 'attack', startup: 9, active: 4, recovery: 26, squash: 0.62,
    anticip: [D('Spine', [1, 0, 0], 0.3), D('RightForeArm', [1, 0, 0], -0.8), D('LeftForeArm', [1, 0, 0], -0.8)],
    strike: [D('Spine', [1, 0, 0], 0.5), D('Spine01', [1, 0, 0], 0.3), D('neck', [1, 0, 0], 0.4), D('RightForeArm', [1, 0, 0], -1.3), D('LeftForeArm', [1, 0, 0], -1.3), D('RightArm', [0, 0, 1], -0.3), D('LeftArm', [0, 0, 1], 0.3), D('RightUpLeg', [1, 0, 0], -0.8), D('LeftUpLeg', [1, 0, 0], 0.8), D('RightLeg', [1, 0, 0], 0.9), D('LeftLeg', [1, 0, 0], 0.9)],
  },
  // Utility — Mie-schild: eerst dekken, dan een zware twee-hands-plow.
  mieSchild: {
    kind: 'attack', startup: 12, active: 6, recovery: 20,
    anticip: [D('RightForeArm', [1, 0, 0], -1.2), D('LeftForeArm', [1, 0, 0], -1.2), D('RightArm', [0, 0, 1], -0.4), D('LeftArm', [0, 0, 1], 0.4), D('Spine', [1, 0, 0], -0.1)],
    strike: [D('Spine', [1, 0, 0], 0.22), D('RightArm', [0, 0, 1], -1.15), D('LeftArm', [0, 0, 1], 1.15), D('RightForeArm', [1, 0, 0], -0.3), D('LeftForeArm', [1, 0, 0], -0.3)],
  },
  // Utility — Drijf: zwevende veeg (armen wijd, knieën opgetrokken).
  drijf: {
    kind: 'attack', startup: 8, active: 6, recovery: 18, squash: 1.08,
    anticip: [D('Spine', [1, 0, 0], 0.1), D('RightArm', [0, 0, 1], -0.3), D('LeftArm', [0, 0, 1], 0.3)],
    strike: [D('Spine', [1, 0, 0], -0.12), D('RightArm', [1, 0, 0], 0.4), D('RightArm', [0, 0, 1], -0.8), D('LeftArm', [1, 0, 0], 0.4), D('LeftArm', [0, 0, 1], 0.8), D('RightUpLeg', [1, 0, 0], -0.5), D('LeftUpLeg', [1, 0, 0], 0.5), D('RightLeg', [1, 0, 0], 0.6), D('LeftLeg', [1, 0, 0], 0.6)],
  },
  // Utility — Schaduw-slag: snelle counter-poke (rechte uithaal + stapje).
  schaduwSlag: {
    kind: 'attack', startup: 6, active: 4, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.3), D('Spine', [1, 0, 0], -0.08)],
    strike: [D('RightArm', [0, 0, 1], -1.6), D('RightForeArm', [1, 0, 0], -0.45), D('Spine', [1, 0, 0], 0.18), D('LeftUpLeg', [1, 0, 0], 0.25), D('LeftLeg', [1, 0, 0], 0.2), D('LeftArm', [0, 0, 1], 0.3)],
  },
  // Boss — Koude Golf: brede, trage twee-hands-golf met wijde stance.
  koudeGolf: {
    kind: 'attack', startup: 16, active: 6, recovery: 24,
    anticip: [D('RightArm', [0, 0, 1], 0.5), D('LeftArm', [0, 0, 1], -0.5), D('Spine', [1, 0, 0], -0.15), D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5)],
    strike: [D('Spine', [1, 0, 0], 0.18), D('RightArm', [0, 0, 1], -1.15), D('LeftArm', [0, 0, 1], 1.15), D('RightForeArm', [1, 0, 0], -0.4), D('LeftForeArm', [1, 0, 0], -0.4), D('RightUpLeg', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.2)],
  },

  // ── 2e specials ──────────────────────────────────────────────────────────────
  // Anti-airs (dp/623): rijzende uithaal — slagarm omhoog (X+), lijf strekt.
  frituurStoot: {
    kind: 'attack', startup: 5, active: 6, recovery: 24, squash: 1.1,
    anticip: [D('RightArm', [1, 0, 0], -0.3), D('Spine', [1, 0, 0], 0.12)],
    strike: [D('RightArm', [1, 0, 0], 1.6), D('RightForeArm', [1, 0, 0], -0.3), D('Spine', [1, 0, 0], -0.12), D('LeftArm', [0, 0, 1], 0.4), D('RightUpLeg', [1, 0, 0], 0.2)],
  },
  korstStoot: {
    kind: 'attack', startup: 6, active: 6, recovery: 24, squash: 1.1,
    anticip: [D('RightArm', [1, 0, 0], -0.3), D('Spine', [1, 0, 0], 0.12)],
    strike: [D('RightArm', [1, 0, 0], 1.55), D('RightForeArm', [1, 0, 0], -0.3), D('Spine', [1, 0, 0], -0.1), D('LeftArm', [0, 0, 1], 0.4), D('RightUpLeg', [1, 0, 0], 0.2)],
  },
  kipWip: {
    kind: 'attack', startup: 4, active: 5, recovery: 22, squash: 1.12,
    anticip: [D('RightArm', [1, 0, 0], -0.25)],
    strike: [D('RightArm', [1, 0, 0], 1.7), D('RightForeArm', [1, 0, 0], -0.25), D('Spine', [1, 0, 0], -0.1), D('LeftArm', [0, 0, 1], 0.3)],
  },
  loempiaWervel: {
    kind: 'attack', startup: 5, active: 6, recovery: 22, squash: 1.1,
    anticip: [D('RightArm', [1, 0, 0], -0.2), D('LeftArm', [1, 0, 0], -0.2)],
    strike: [D('RightArm', [1, 0, 0], 1.5), D('LeftArm', [1, 0, 0], 1.5), D('RightForeArm', [1, 0, 0], -0.3), D('LeftForeArm', [1, 0, 0], -0.3), D('Spine', [1, 0, 0], -0.1)],
  },
  // Kaassoufflé — Soufflé-zweef: snelle poke met achterover-lean (evasief).
  souffleZweef: {
    kind: 'attack', startup: 7, active: 4, recovery: 16,
    anticip: [D('RightArm', [0, 0, 1], 0.3), D('Spine', [1, 0, 0], -0.12)],
    strike: [D('RightArm', [0, 0, 1], -1.35), D('RightForeArm', [1, 0, 0], -0.4), D('Spine', [1, 0, 0], -0.1), D('LeftArm', [0, 0, 1], 0.3)],
  },
  // Berenhap — Beren-stamp: trage zware twee-hands-smash naar beneden + stomp.
  berenStamp: {
    kind: 'attack', startup: 14, active: 5, recovery: 24,
    anticip: [D('RightArm', [1, 0, 0], 0.6), D('LeftArm', [1, 0, 0], 0.6), D('Spine', [1, 0, 0], -0.15)],
    strike: [D('Spine', [1, 0, 0], 0.3), D('RightArm', [0, 0, 1], -1.3), D('LeftArm', [0, 0, 1], 1.3), D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5), D('RightUpLeg', [1, 0, 0], -0.4), D('RightLeg', [1, 0, 0], 0.5)],
  },
  // Bitterbal — Stormloop: gestroomlijnde dash-gap-closer.
  bitterStorm: {
    kind: 'attack', startup: 8, active: 5, recovery: 20, squash: 1.05,
    anticip: [D('Spine', [1, 0, 0], -0.1), D('RightArm', [0, 0, 1], 0.4), D('LeftArm', [0, 0, 1], -0.4)],
    strike: [D('Spine', [1, 0, 0], 0.3), D('neck', [1, 0, 0], 0.15), D('RightArm', [0, 0, 1], -0.5), D('LeftArm', [0, 0, 1], 0.5), D('RightForeArm', [1, 0, 0], -0.3), D('LeftForeArm', [1, 0, 0], -0.3)],
  },
  // Bamiblok + Eierbal — projectiel-worp: één-hands worp vooruit-omhoog.
  bamiBal: {
    kind: 'attack', startup: 13, active: 6, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.4), D('RightForeArm', [1, 0, 0], -0.3)],
    strike: [D('RightArm', [0, 0, 1], -1.0), D('RightArm', [1, 0, 0], 0.4), D('RightForeArm', [1, 0, 0], -0.4), D('Spine', [1, 0, 0], 0.12), D('LeftArm', [0, 0, 1], 0.3)],
  },
  eiWorp: {
    kind: 'attack', startup: 11, active: 5, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.4), D('RightForeArm', [1, 0, 0], -0.3)],
    strike: [D('RightArm', [0, 0, 1], -1.05), D('RightArm', [1, 0, 0], 0.5), D('RightForeArm', [1, 0, 0], -0.4), D('Spine', [1, 0, 0], 0.1), D('LeftArm', [0, 0, 1], 0.3)],
  },

  // ── Supers (Vetslag) — bespoke, dramatischer dan de signature ───────────────
  // Gekeyd op de super-id (<sigId>_super); zelfde frame-data als de signature
  // (makeSuper behoudt startup/active/recovery) → frame-accuraat. Vollere body-
  // commit: diepere coil, grotere uithaal, lunge/stance erbij.
  frikandelSpeciaal_super: {
    kind: 'attack', startup: 12, active: 6, recovery: 18,
    anticip: [D('RightArm', [0, 0, 1], 0.7), D('LeftArm', [0, 0, 1], -0.7), D('RightForeArm', [1, 0, 0], -0.8), D('LeftForeArm', [1, 0, 0], -0.8), D('Spine', [1, 0, 0], -0.25)],
    strike: [D('Spine', [1, 0, 0], 0.32), D('RightArm', [0, 0, 1], -1.5), D('LeftArm', [0, 0, 1], 1.5), D('RightForeArm', [1, 0, 0], -0.55), D('LeftForeArm', [1, 0, 0], -0.55), D('LeftUpLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4), D('RightUpLeg', [1, 0, 0], 0.3)],
  },
  ragoutGolf_super: {
    kind: 'attack', startup: 14, active: 6, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.6), D('LeftArm', [0, 0, 1], -0.6), D('Spine', [1, 0, 0], -0.2)],
    strike: [D('Spine', [1, 0, 0], 0.18), D('RightArm', [1, 0, 0], 0.9), D('RightArm', [0, 0, 1], -0.8), D('LeftArm', [1, 0, 0], 0.9), D('LeftArm', [0, 0, 1], 0.8), D('RightForeArm', [1, 0, 0], -0.4), D('LeftForeArm', [1, 0, 0], -0.4), D('neck', [1, 0, 0], -0.15)],
  },
  kaasFlik_super: {
    kind: 'attack', startup: 10, active: 5, recovery: 22,
    anticip: [D('RightArm', [0, 0, 1], 0.6), D('LeftArm', [0, 0, 1], -0.4), D('Spine', [1, 0, 0], -0.12)],
    strike: [D('RightArm', [0, 0, 1], -1.6), D('RightForeArm', [1, 0, 0], -0.6), D('LeftArm', [0, 0, 1], 1.0), D('LeftForeArm', [1, 0, 0], -0.4), D('Spine', [1, 0, 0], 0.2), D('LeftUpLeg', [1, 0, 0], 0.3)],
  },
  verrePort_super: {
    kind: 'attack', startup: 11, active: 5, recovery: 22,
    anticip: [D('RightArm', [0, 0, 1], 0.5), D('Spine', [1, 0, 0], -0.18), D('RightUpLeg', [1, 0, 0], 0.3)],
    strike: [D('Spine', [1, 0, 0], 0.36), D('RightArm', [0, 0, 1], -1.9), D('RightForeArm', [1, 0, 0], -0.6), D('LeftArm', [0, 0, 1], 0.5), D('LeftUpLeg', [1, 0, 0], 0.7), D('LeftLeg', [1, 0, 0], 0.6), D('RightUpLeg', [1, 0, 0], 0.6)],
  },
  zoef_super: {
    kind: 'attack', startup: 6, active: 4, recovery: 16, squash: 1.12,
    anticip: [D('Spine', [1, 0, 0], -0.15), D('RightArm', [0, 0, 1], 0.6), D('LeftArm', [0, 0, 1], -0.6)],
    strike: [D('Spine', [1, 0, 0], 0.4), D('neck', [1, 0, 0], 0.25), D('RightArm', [0, 0, 1], 0.7), D('LeftArm', [0, 0, 1], -0.7), D('RightForeArm', [1, 0, 0], -0.4), D('LeftForeArm', [1, 0, 0], -0.4), D('RightUpLeg', [1, 0, 0], -0.5), D('LeftUpLeg', [1, 0, 0], 0.5)],
  },
  bowlingRol_super: {
    kind: 'attack', startup: 9, active: 4, recovery: 26, squash: 0.55,
    anticip: [D('Spine', [1, 0, 0], 0.4), D('RightForeArm', [1, 0, 0], -1.0), D('LeftForeArm', [1, 0, 0], -1.0)],
    strike: [D('Spine', [1, 0, 0], 0.6), D('Spine01', [1, 0, 0], 0.4), D('neck', [1, 0, 0], 0.5), D('RightForeArm', [1, 0, 0], -1.5), D('LeftForeArm', [1, 0, 0], -1.5), D('RightArm', [0, 0, 1], -0.4), D('LeftArm', [0, 0, 1], 0.4), D('RightUpLeg', [1, 0, 0], -1.0), D('LeftUpLeg', [1, 0, 0], 1.0), D('RightLeg', [1, 0, 0], 1.1), D('LeftLeg', [1, 0, 0], 1.1)],
  },
  mieSchild_super: {
    kind: 'attack', startup: 12, active: 6, recovery: 20,
    anticip: [D('RightForeArm', [1, 0, 0], -1.4), D('LeftForeArm', [1, 0, 0], -1.4), D('RightArm', [0, 0, 1], -0.5), D('LeftArm', [0, 0, 1], 0.5), D('Spine', [1, 0, 0], -0.18)],
    strike: [D('Spine', [1, 0, 0], 0.3), D('RightArm', [0, 0, 1], -1.35), D('LeftArm', [0, 0, 1], 1.35), D('RightForeArm', [1, 0, 0], -0.4), D('LeftForeArm', [1, 0, 0], -0.4), D('LeftUpLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.4)],
  },
  drijf_super: {
    kind: 'attack', startup: 8, active: 6, recovery: 18, squash: 1.14,
    anticip: [D('Spine', [1, 0, 0], 0.12), D('RightArm', [0, 0, 1], -0.4), D('LeftArm', [0, 0, 1], 0.4)],
    strike: [D('Spine', [1, 0, 0], -0.15), D('RightArm', [1, 0, 0], 0.6), D('RightArm', [0, 0, 1], -1.0), D('LeftArm', [1, 0, 0], 0.6), D('LeftArm', [0, 0, 1], 1.0), D('RightUpLeg', [1, 0, 0], -0.7), D('LeftUpLeg', [1, 0, 0], 0.7), D('RightLeg', [1, 0, 0], 0.8), D('LeftLeg', [1, 0, 0], 0.8)],
  },
  schaduwSlag_super: {
    kind: 'attack', startup: 6, active: 4, recovery: 20,
    anticip: [D('RightArm', [0, 0, 1], 0.5), D('Spine', [1, 0, 0], -0.14), D('LeftArm', [0, 0, 1], -0.3)],
    strike: [D('RightArm', [0, 0, 1], -1.8), D('RightForeArm', [1, 0, 0], -0.55), D('LeftArm', [0, 0, 1], 1.1), D('Spine', [1, 0, 0], 0.26), D('LeftUpLeg', [1, 0, 0], 0.4), D('LeftLeg', [1, 0, 0], 0.35)],
  },
  koudeGolf_super: {
    kind: 'attack', startup: 16, active: 6, recovery: 24,
    anticip: [D('RightArm', [0, 0, 1], 0.7), D('LeftArm', [0, 0, 1], -0.7), D('Spine', [1, 0, 0], -0.22), D('RightForeArm', [1, 0, 0], -0.7), D('LeftForeArm', [1, 0, 0], -0.7)],
    strike: [D('Spine', [1, 0, 0], 0.24), D('RightArm', [0, 0, 1], -1.35), D('LeftArm', [0, 0, 1], 1.35), D('RightForeArm', [1, 0, 0], -0.5), D('LeftForeArm', [1, 0, 0], -0.5), D('RightUpLeg', [1, 0, 0], 0.35), D('LeftUpLeg', [1, 0, 0], 0.35)],
  },

  // ── Reacties (hold-poses) ──
  hitstun: {
    kind: 'hold',
    hold: [
      D('Spine', [1, 0, 0], -0.4), // achterover deinzen
      D('neck', [1, 0, 0], -0.3),
      D('RightArm', [0, 0, 1], -0.3),
      D('LeftArm', [0, 0, 1], 0.3),
    ],
  },
  ko: {
    kind: 'hold',
    hold: [
      D('Spine', [1, 0, 0], -1.0), // ver achterover (knock-down)
      D('Spine01', [1, 0, 0], -0.5),
      D('RightUpLeg', [1, 0, 0], -0.6), // knieën knikken (mirror op X)
      D('LeftUpLeg', [1, 0, 0], 0.6),
      D('RightLeg', [1, 0, 0], 0.8),
      D('LeftLeg', [1, 0, 0], 0.8),
    ],
  },
};

const ATTACK_REST_FRAMES = 22; // pauze tussen herhalingen in de lab-preview

/** Cartoon-squash (Y-schaal) voor een actie; 1 = geen. */
export function actionSquash(id: string): number {
  return ACTIONS[id]?.squash ?? 1;
}

/** Deltas voor een actie op tijd t (sec). Attacks loopen voor de preview. */
export function actionDeltas(id: string, t: number): BoneDelta[] {
  const def = ACTIONS[id];
  if (!def) return [];
  if (def.kind === 'hold') return def.hold ?? [];
  if (def.kind === 'loop') return def.loop ? def.loop(t) : [];
  // attack: loop het frame-venster
  const s = def.startup ?? 4;
  const a = def.active ?? 3;
  const r = def.recovery ?? 8;
  const cycle = s + a + r + ATTACK_REST_FRAMES;
  const f = (t * 60) % cycle;
  const { wA, wS } = evalPhase({ startup: s, active: a, recovery: r } as Move, f);
  return [...scaleDeltas(def.anticip ?? [], wA), ...scaleDeltas(def.strike ?? [], wS)];
}
